import { useEffect, useMemo, useState } from 'react'
import type { ResultatIdentite } from '../../api/candidatures'
import {
  apercuDesignation,
  champsVisibles,
  FORMES_JURIDIQUES,
  LIBELLE_CHAMP,
  LIBELLE_FORME,
  LIBELLE_STATUT,
  LIBELLE_TYPE_PIECE,
  libelleManquants,
  saisieDepuis,
  statutsProposes,
  TYPES_PIECE,
  type IdentiteSaisie,
  type StatutJuridique,
  type SujetContrat,
} from '../../onboarding/identite'
import type { LigneIdentite } from '../../onboarding/types'
import { Erreur, useAction } from './Briques'

/**
 * Identité juridique d'un partenaire (migration 0050) : le STATUT choisit les champs à saisir, les pièces
 * à exiger et la façon dont la partie sera désignée dans le contrat. Aperçu EN DIRECT de la désignation
 * (le même module que les edge functions) et liste de ce qu'il manque ; la base valide à l'enregistrement.
 */
export function BlocIdentite({
  sujet,
  ligne,
  nomEtablissement,
  surEnregistrer,
  surStatut,
}: {
  sujet: SujetContrat
  /** Ce que la base contient (`null` : rien de saisi). */
  ligne: LigneIdentite | null
  /** Marchand : le nom de l'établissement, proposé comme nom commercial par défaut. */
  nomEtablissement?: string
  surEnregistrer: (saisie: IdentiteSaisie) => Promise<ResultatIdentite>
  /** Prévient la fiche dès qu'un statut est CHOISI (avant enregistrement), pour que la liste des pièces suive. */
  surStatut?: (statut: StatutJuridique | null) => void
}) {
  const [saisie, setSaisie] = useState<IdentiteSaisie>(() => saisieDepuis(ligne))
  const [manquantsServeur, setManquantsServeur] = useState<string[] | null>(null)
  const { occupe, erreur, info, lancer } = useAction()

  // La base a changé (rechargement du dossier) : on repart de ce qu'elle contient.
  const empreinte = useMemo(() => JSON.stringify(ligne), [ligne])
  useEffect(() => {
    setSaisie(saisieDepuis(ligne))
    setManquantsServeur(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empreinte])

  const statut = saisie.statut_juridique
  useEffect(() => {
    surStatut?.((statut || null) as StatutJuridique | null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statut])
  const champs = champsVisibles(sujet, statut)
  const apercu = apercuDesignation(sujet, saisie)
  const modifie = JSON.stringify(saisie) !== JSON.stringify(saisieDepuis(ligne))

  const maj = (cle: string, v: string) => setSaisie((s) => ({ ...s, [cle]: v }))

  function enregistrer() {
    void lancer(async () => {
      const r = await surEnregistrer(saisie)
      setManquantsServeur(r.manquants)
      return r.manquants.length === 0
        ? 'Identité enregistrée : rien ne manque.'
        : `Identité enregistrée. Il manque encore : ${libelleManquants(r.manquants)}.`
    })
  }

  return (
    <>
      <label htmlFor={`statut-${sujet}`}>Statut juridique</label>
      <select
        id={`statut-${sujet}`}
        value={statut}
        onChange={(e) => setSaisie(pourStatut(saisie, e.target.value as StatutJuridique | ''))}
      >
        <option value="">— Choisir —</option>
        {statutsProposes(sujet).map((s) => (
          <option key={s} value={s}>{LIBELLE_STATUT[s]}</option>
        ))}
      </select>
      {statut === '' && (
        <p className="dos-note" style={{ marginTop: 6 }}>
          Le statut décide des pièces exigées et de la façon dont la partie est désignée dans le contrat. Il doit
          être renseigné avant l'envoi du contrat.
        </p>
      )}

      {champs.length > 0 && (
        <div className="ident-grille">
          {champs.map(({ cle, requis }) => (
            <div key={cle} className={`ident-champ${cle === 'siege_adresse' || cle === 'adresse_personne' ? ' large' : ''}`}>
              <label htmlFor={`ident-${sujet}-${cle}`}>
                {LIBELLE_CHAMP[cle] ?? cle}
                {requis ? <span className="ident-req"> *</span> : <span className="dos-note"> (facultatif)</span>}
              </label>
              {cle === 'forme_juridique' ? (
                <select id={`ident-${sujet}-${cle}`} value={saisie[cle] ?? ''} onChange={(e) => maj(cle, e.target.value)}>
                  <option value="">— Choisir —</option>
                  {FORMES_JURIDIQUES.map((f) => (
                    <option key={f} value={f}>{f === 'autre' ? 'Autre' : `${f} — ${LIBELLE_FORME[f]}`}</option>
                  ))}
                </select>
              ) : cle === 'type_piece' ? (
                <select id={`ident-${sujet}-${cle}`} value={saisie[cle] ?? ''} onChange={(e) => maj(cle, e.target.value)}>
                  <option value="">— Choisir —</option>
                  {TYPES_PIECE.map((t) => (
                    <option key={t} value={t}>{LIBELLE_TYPE_PIECE[t]}</option>
                  ))}
                </select>
              ) : (
                <input
                  id={`ident-${sujet}-${cle}`}
                  type={cle === 'date_naissance' ? 'date' : cle === 'capital_fcfa' ? 'number' : 'text'}
                  min={cle === 'capital_fcfa' ? 1 : undefined}
                  value={saisie[cle] ?? ''}
                  placeholder={cle === 'nom_commercial' ? nomEtablissement : undefined}
                  onChange={(e) => maj(cle, e.target.value)}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {statut !== '' && (
        <div className="ident-apercu" aria-live="polite">
          <b>Désignation dans le contrat</b>
          {apercu.texte ? (
            <p>{apercu.texte}</p>
          ) : (
            <p className="dos-note">
              Aperçu indisponible. Il manque : <b>{libelleManquants(apercu.manquants)}</b>.
            </p>
          )}
          <p className="dos-note">Formule à valider par le juriste (voir « Modèles de contrat »).</p>
        </div>
      )}

      <div className="dos-champs">
        <button className="btn btn-accent btn-sm" disabled={occupe || statut === '' || !modifie} onClick={enregistrer}>
          {occupe ? 'Enregistrement…' : "Enregistrer l'identité"}
        </button>
        {!modifie && statut !== '' && ligne !== null && <span className="dos-note">À jour.</span>}
        {manquantsServeur !== null && manquantsServeur.length === 0 && !modifie && (
          <span className="badge badge-green">Identité complète</span>
        )}
      </div>
      <Erreur texte={erreur} />
      {info && <div className="dos-ok">{info}</div>}
    </>
  )
}

/** Changer de statut garde ce que la personne a déjà saisi (les champs communs), l'autre statut les ignorera. */
function pourStatut(avant: IdentiteSaisie, statut: StatutJuridique | ''): IdentiteSaisie {
  return { ...avant, statut_juridique: statut }
}
